import Cookies from "universal-cookie";
import { useEffect, useMemo, useState } from "react";
import { withTransaction } from "@elastic/apm-rum-react";
import { useRouter } from "next/router";
import Header from "@/src/views/components/Header";
import LandingPage from "@/src/views/products/landing/containers/intro";
import { useDevice, useGetLocalStorage } from "@/src/services/hooks/index";
import { LandingLoginProvider } from "../views/products/landing/context/LandingLoginContext";

function Index() {
  const router = useRouter();
  const device: "mobile" | "desktop" | undefined = useDevice();
  const cookie = useMemo(() => new Cookies(), []);
  const session = cookie.get("user") !== undefined;
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL;
  const urlMobile = process.env.NEXT_PUBLIC_MOBILE_URL;
  const data = useGetLocalStorage("data");
  const [shouldShowLanding, setShouldShowLanding] = useState(false);

  const userData = useMemo(() => {
    if (typeof data !== "string") {
      console.log("[Index] Local storage `data` missing or not a string.", {
        rawData: data,
      });
      return null;
    }
    try {
      const parsed = JSON.parse(data);
      console.log("[Index] Parsed userData from local storage.", parsed);
      return parsed;
    } catch (error) {
      console.log("[Index] Failed to parse local storage `data`.", {
        error,
        rawData: data,
      });
      return null;
    }
  }, [data]);

  useEffect(() => {
    console.log("[Index] Render effect triggered.", {
      device,
      session,
      hasUserData: Boolean(userData),
    });

    if (device !== "desktop") {
      console.log("[Index] Device is not desktop; skipping landing check.", {
        device,
      });
      setShouldShowLanding(false);
      return;
    }

    if (!session) {
      console.log("[Index] No session cookie found; showing landing page.");
      setShouldShowLanding(true);
      return;
    }

    const accesses = userData?.access?.filter((access: any) =>
      access?.device?.some((d: any) => d.device === device),
    );

    if (!accesses?.length) {
      console.log(
        "[Index] No matching access found; clearing storage and showing landing.",
        {
          accesses,
          userData,
        },
      );
      if (typeof window !== "undefined") {
        localStorage.clear();
        sessionStorage.clear();
      }
      const cookieNames = ["user", "userData", "loginDone", "userLogActivity"];
      const pathSegments = window.location.pathname
        .split("/")
        .filter(Boolean)
        .reduce((paths: string[], segment: string) => {
          const prev = paths[paths.length - 1] || "";
          paths.push(`${prev}/${segment}`);
          return paths;
        }, []);

      cookieNames.forEach((name) => {
        cookie.remove(name, { path: "/" });
        pathSegments.forEach((p: string) => cookie.remove(name, { path: p }));
      });
      setShouldShowLanding(true);
      return;
    }

    setShouldShowLanding(false);

    const access = accesses[0];
    const routeDevice = access?.device?.find((d: any) => d.device === device);
    const redirectUri =
      accesses.length > 1 ? "/home" : (routeDevice?.uri ?? "/home");

    console.log("[Index] Redirecting desktop session to dashboard.", {
      redirectUri,
      accessesCount: accesses.length,
    });

    router.push(`${baseUrl ?? ""}${redirectUri}`);
  }, [baseUrl, cookie, device, router, session, userData]);

  useEffect(() => {
    console.log("[Index] Landing flag updated.", { shouldShowLanding });
  }, [shouldShowLanding]);

  useEffect(() => {
    console.log("[Index] Checking mobile redirection.", { device, urlMobile });
    if (device === "mobile" && typeof window !== "undefined" && urlMobile) {
      console.log("[Index] Redirecting to mobile URL.", { urlMobile });
      window.location.assign(urlMobile);
    }
  }, [device, urlMobile]);

  if (device === "desktop" && shouldShowLanding) {
    const title = "Tingkatkan produktivitas tenaga pemasar dengan BRIBRAIN";
    return (
      <>
        <Header title={title} />
        <LandingLoginProvider>
          <LandingPage id={process.env.NEXT_PUBLIC_LANDINGPAGE} />
        </LandingLoginProvider>
      </>
    );
  }

  console.log("[Index] Returning fallback container.", {
    device,
    shouldShowLanding,
  });

  return <div />;
}

export default withTransaction("Index", "component")(Index);
